import { ApolloClient, QueryOptions, OperationVariables } from "@apollo/client";
import { isEqual } from "lodash";
import { AgGrid } from "@jsq/ds/core";
import { GetFundsListDocument } from "#graphql/supergraphHooks";
import {
  type GetFundsListQuery,
  type AccountObjectTypeSortEnum,
} from "#graphql/supergraphTypes";
import { clientContext } from "#utils/graphql_utils";
import { FundRow } from "./types";

const DEFAULT_PAGE_SIZE = 50;
const QUERY_HAS_MORE_ROWS = -1;

/**
 * Attempts to calculate a cursor for a given offset.
 * This assumes the backend uses base64-encoded offsets in the format "arrayconnection:{offset}".
 *
 * @param offset - The zero-based offset to encode as a cursor
 * @returns A base64-encoded cursor string
 */
export const calculateCursorFromOffset = (offset: number): string => {
  // Common Relay cursor format: base64("arrayconnection:{offset}")
  return btoa(`arrayconnection:${offset}`);
};

/**
 * Attempts to decode a cursor to extract the offset.
 * Returns null if the cursor doesn't follow the expected format.
 *
 * @param cursor - The cursor string to decode
 * @returns The offset number, or null if decoding fails
 */
export const decodeCursorToOffset = (cursor: string): number | null => {
  try {
    const decoded = atob(cursor);
    const match = decoded.match(/^arrayconnection:(\d+)$/);
    if (match) {
      return parseInt(match[1], 10);
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Maps AG Grid column IDs to GraphQL AccountObjectTypeSortEnum values
 */
const mapSortFieldToGraphQL = (
  colId: string,
  sortDirection: "asc" | "desc",
): AccountObjectTypeSortEnum | null => {
  const sortMap: Record<
    string,
    { asc: AccountObjectTypeSortEnum; desc: AccountObjectTypeSortEnum }
  > = {
    name: {
      asc: "NAME_ASC",
      desc: "NAME_DESC",
    },
    arenaName: {
      asc: "ARENA_ID_ASC",
      desc: "ARENA_ID_DESC",
    },
    // Add more mappings as needed when additional sortable columns are added
  };

  const mapping = sortMap[colId];
  if (!mapping) {
    return null;
  }

  return mapping[sortDirection];
};

/**
 * Converts AG Grid sort model to GraphQL sort enum array.
 * Returns null if no valid sort parameters are provided.
 */
export const getSortVars = (
  sortItems: AgGrid.SortModelItem[] | null,
): AccountObjectTypeSortEnum[] | null => {
  if (!sortItems || sortItems.length === 0) {
    return null;
  }

  const querySorts: AccountObjectTypeSortEnum[] = [];
  for (const item of sortItems) {
    const sortEnum = mapSortFieldToGraphQL(item.colId, item.sort);
    if (sortEnum) {
      querySorts.push(sortEnum);
    }
  }

  return querySorts.length > 0 ? querySorts : null;
};

/**
 * Transforms GraphQL query response to AG Grid row format.
 * Returns an empty array if query returns no data.
 * @throws Error if required fields (name, arena.id, arena.name) are missing
 */
export const rowsFromQuery = (data: GetFundsListQuery | undefined | null): FundRow[] => {
  if (!data?.investmentEntitiesByArena?.edges) {
    return [];
  }

  return data.investmentEntitiesByArena.edges.map((edge) => {
    // Validate required fields
    if (!edge.node.name) {
      throw new Error(`Fund with id ${edge.node.id} is missing required field: name`);
    }
    if (!edge.node.arena?.id) {
      throw new Error(
        `Fund ${edge.node.name} (id: ${edge.node.id}) is missing required field: arena.id`,
      );
    }
    if (!edge.node.arena?.name) {
      throw new Error(
        `Fund ${edge.node.name} (id: ${edge.node.id}) is missing required field: arena.name`,
      );
    }
    if (!edge.node.arena?.domain) {
      throw new Error(
        `Fund ${edge.node.name} (id: ${edge.node.id}) is missing required field: arena.domain`,
      );
    }

    return {
      id: edge.node.id,
      name: edge.node.name,
      arenaId: edge.node.arena.id,
      arenaName: edge.node.arena.name,
      arenaDomain: edge.node.arena.domain ?? "",
      transactionBankAccounts: (edge.node.transactionBankAccounts ?? []).map((account) => ({
        id: account.id,
        bankName: account.bankName ?? null,
        bankInfoRedacted: account.bankInfoRedacted ?? null,
      })),
    };
  });
};

/**
 * Props for FundsGraphqlDataSource
 */
export interface FundsGraphqlDataSourceProps {
  onSortsChange?: (sorts: AccountObjectTypeSortEnum[] | null) => void;
}

/**
 * Server-side data source for AG Grid that handles pagination, sorting, and filtering
 * for the funds list using cursor-based pagination from GraphQL.
 *
 * This data source converts AG Grid's offset-based pagination to cursor-based pagination
 * used by the GraphQL API.
 */
export class FundsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<object>;
  private onSortsChange?: (sorts: AccountObjectTypeSortEnum[] | null) => void;

  // Cursor tracking for pagination
  private cursors: Map<number, string> = new Map();
  private totalCount: number | null = null;

  // Track previous sort state to detect changes
  private previousSorts: AccountObjectTypeSortEnum[] | null = null;

  constructor(apolloClient: ApolloClient<object>, props?: FundsGraphqlDataSourceProps) {
    this.client = apolloClient;
    this.onSortsChange = props?.onSortsChange;
  }

  /**
   * Handles error cases by calling the AG Grid fail callback
   */
  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  /**
   * Calculates the total row count for AG Grid
   *
   * TEMPORARY: We're returning the total count to test if cursors are base64-encoded offsets.
   * If this works, we can enable "jump to last page" functionality.
   * If it doesn't work, we should revert to hiding the count.
   */
  public calculateRowCount(
    count: number | null | undefined,
    rowDataLength: number,
    startRow: number | null | undefined,
    endRow: number | null | undefined,
    hasNextPage: boolean,
  ): number {
    if (typeof count === "number") {
      return count;
    }

    if (
      startRow === null ||
      startRow === undefined ||
      endRow === null ||
      endRow === undefined
    ) {
      return rowDataLength;
    }

    if (rowDataLength < endRow - startRow) {
      return startRow + rowDataLength;
    }

    if (hasNextPage) {
      return QUERY_HAS_MORE_ROWS;
    }

    return startRow + rowDataLength;
  }

  /**
   * Handles successful query response
   */
  public handleQueryResults(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetFundsListQuery,
  ): void {
    if (!response.investmentEntitiesByArena) {
      this.handleError(params);
      return;
    }

    const { pageInfo, count } = response.investmentEntitiesByArena;
    const rowData = rowsFromQuery(response);
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 0;

    // Store the end cursor for the next page
    if (pageInfo?.endCursor) {
      this.cursors.set(endRow, pageInfo.endCursor);
    }

    // Store total count if available
    if (typeof count === "number") {
      this.totalCount = count;
    }

    const rowCount = this.calculateRowCount(
      count,
      rowData.length,
      startRow,
      endRow,
      pageInfo?.hasNextPage ?? false,
    );

    params.success({
      rowData,
      rowCount,
    });

    // Handle no-rows overlay for server-side row model
    if (rowCount === 0 && startRow === 0) {
      params.api?.showNoRowsOverlay();
    } else if (rowCount > 0) {
      params.api?.hideOverlay();
    }
  }

  /**
   * Builds GraphQL query options from AG Grid request parameters
   */
  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetFundsListQuery> {
    const sorts = getSortVars(params.request.sortModel);
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 0;
    const limit = endRow - startRow;

    // Reset cursors when sort changes
    if (!isEqual(this.previousSorts, sorts)) {
      this.resetCursors();
      this.previousSorts = sorts;
    }

    if (this.onSortsChange) {
      this.onSortsChange(sorts);
    }

    // Determine the cursor to use for pagination
    let after: string | undefined;

    if (startRow === 0) {
      // At the beginning, no cursor needed
      after = undefined;
    } else {
      // Try to get the cursor from our cache
      after = this.cursors.get(startRow);

      // If we don't have the cursor (e.g., user jumped to last page),
      // try to calculate it from the offset
      if (!after && startRow > 0) {
        // Calculate what the cursor should be for the previous page
        // This assumes cursors are base64-encoded offsets
        after = calculateCursorFromOffset(startRow - 1);
      }
    }

    return {
      query: GetFundsListDocument,
      variables: {
        first: limit > 0 ? limit : DEFAULT_PAGE_SIZE,
        after: after,
        sort: sorts ?? ["NAME_ASC"],
      },
      context: clientContext(),
      fetchPolicy: "no-cache",
    };
  }

  /**
   * Executes the GraphQL query
   */
  public executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    return this.client
      .query<GetFundsListQuery>(queryOptions)
      .then((response) => {
        this.handleQueryResults(params, response.data);
      })
      .catch(() => {
        this.handleError(params);
      });
  }

  /**
   * Main entry point called by AG Grid to fetch rows
   */
  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }

  /**
   * Resets the cursor cache (useful when filters or sorts change)
   */
  public resetCursors(): void {
    this.cursors.clear();
    this.totalCount = null;
    this.previousSorts = null;
  }
}
